import type { StaffOrder } from "../api/types";
import { BenchStamp } from "../ui/components";
import { elapsed, exclusionLabel, minutesSince, money, timeOnly } from "../ui/format";

export function OrderLines({ order }: { order: StaffOrder }) {
  return (
    <ul className="lines">
      {order.lines.map((line, index) => (
        <li key={`${line.productId}-${index}`}>
          <div className="line-title">
            <b className="data">{line.qty}×</b> {line.title}
          </div>
          {line.modifiers.length > 0 ? (
            <div className="mods">
              {line.modifiers.map((modifier, modifierIndex) =>
                modifier.exclusion ? (
                  // Исключения выделяются: пропустить их — переделывать блюдо (п. 11.2 ТЗ).
                  <span className="mod-out" key={modifierIndex}>
                    {exclusionLabel(modifier.title)}
                  </span>
                ) : (
                  <span className="mod-add" key={modifierIndex}>
                    + {modifier.title}
                    {modifier.qty > 1 ? ` ×${modifier.qty}` : ""}
                  </span>
                )
              )}
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function PhoneLink({ order }: { order: StaffOrder }) {
  if (!order.phoneVisible) {
    return (
      <p className="tiny muted">
        Телефон скрыт: {order.customerPhone}
      </p>
    );
  }
  return (
    <a className="phone-link" href={`tel:${order.customerPhone}`}>
      📞 {order.customerPhone}
    </a>
  );
}

export function OrderNotes({ order }: { order: StaffOrder }) {
  return (
    <>
      {order.comment ? <p className="comment-box">Клиент: {order.comment}</p> : null}
      {order.internalNotes.map((note, index) => (
        <p className="note-box" key={index}>
          <strong>{note.author}</strong> · {timeOnly(note.at)}
          <br />
          {note.text}
        </p>
      ))}
    </>
  );
}

/**
 * Заказ считается просроченным, когда время с оплаты превысило обещанный
 * клиенту прогноз (п. 12 ТЗ).
 */
export function isOverdue(order: StaffOrder, now: number): boolean {
  if (!order.paidAt) return false;
  if (order.status === "delivered" || order.status === "cancelled") return false;
  return minutesSince(order.paidAt, now) > order.etaMinutes;
}

export function TicketHead({ order, now }: { order: StaffOrder; now: number }) {
  const late = isOverdue(order, now);

  return (
    <div className="ticket-head">
      <div>
        <div className="ticket-no">№{order.number}</div>
        <div className="timers">
          <span className={`timer ${late ? "late" : ""}`}>
            с оплаты <b>{elapsed(order.paidAt, now)}</b>
          </span>
          <span className="timer">
            прогноз <b>{order.etaMinutes} мин</b>
          </span>
        </div>
      </div>
      {/* У заказа с окна лавочки нет: вместо номера — метка «на вынос»,
          чтобы повар видел, кому заказ отдавать. */}
      {order.benchId ? <BenchStamp bench={order.benchId} /> : <span className="chip chip-pickup">На вынос</span>}
    </div>
  );
}

/** Отметка о согласованной правке состава — заказ на кухне мог измениться. */
export function CorrectedChip({ order }: { order: StaffOrder }) {
  if (!order.correctedAt) return null;
  return <span className="chip chip-corrected">Состав изменён</span>;
}

export function MoneyLine({ order }: { order: StaffOrder }) {
  return (
    <p className="tiny">
      <span className="num">{money(order.total)}</span>
      {order.discount ? <span className="muted"> · скидка {money(order.discount)}</span> : null}
      {order.promo ? <span className="muted"> · {order.promo}</span> : null}
      {order.refundAmount ? <span className="muted"> · возврат {money(order.refundAmount)}</span> : null}
    </p>
  );
}
